import React, { useEffect, useState, useMemo } from 'react';
import apiService from '../../../services/apiService';
import CustomerJourneyModal from './CustomerJourneyModal';
import { renderCustomerNameWithTravels } from '../../../utils/nameFormatter';

interface CompletedDeliveriesProps {
  onBack?: () => void;
}

const WORKFLOW_STEPS = [
  'Booking',
  'Document Complete',
  'Loan Login',
  'Verification',
  'Account Opening',
  'Agreement',
  'Down Payment',
  'Disbursement',
  'RTO (Passing)',
  'Delivery'
];

const CompletedDeliveries: React.FC<CompletedDeliveriesProps> = ({ onBack }) => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [carFilter, setCarFilter] = useState('all');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  const fetchDeliveredCustomers = async () => {
    setLoading(true);
    try {
      const res = await apiService.getCrmCustomers({ type: 'delivered' });
      if (res.success) {
        setCustomers(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching delivered customers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveredCustomers();
  }, []);

  // Filter customers that reached Delivery stage or completed all steps
  const deliveredCustomers = useMemo(() => {
    return customers.filter(cust => {
      const steps = cust.crm_workflow_steps || cust.workflow_steps || [];
      const deliveryStep = steps.find((s: any) => s.step_name === 'Delivery');
      const isDeliveryDone = deliveryStep && deliveryStep.status === 'Done';
      const allStepsDone = steps.length > 0 && steps.every((s: any) => s.status === 'Done');
      const isStatusConverted = cust.status === 'Converted' || cust.status === 'Delivered';
      
      return isDeliveryDone || allStepsDone || isStatusConverted;
    });
  }, [customers]);

  // Filtered delivered leads based on search & car filter
  const filteredList = useMemo(() => {
    return deliveredCustomers.filter(cust => {
      if (search) {
        const q = search.toLowerCase();
        const nameMatch = cust.customer_name?.toLowerCase().includes(q);
        const phoneMatch = cust.phone?.includes(q);
        const carMatch = cust.car_details?.toLowerCase().includes(q) || cust.interested_car?.toLowerCase().includes(q);
        if (!nameMatch && !phoneMatch && !carMatch) return false;
      }
      if (carFilter !== 'all') {
        const car = (cust.car_details || cust.interested_car || '').toLowerCase();
        if (!car.includes(carFilter.toLowerCase())) return false;
      }
      return true;
    });
  }, [deliveredCustomers, search, carFilter]);

  // Metric stats
  const totalDelivered = deliveredCustomers.length;
  const thisMonthDelivered = deliveredCustomers.filter(c => {
    const d = new Date(c.updated_at || c.created_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  const avgDaysToDelivery = useMemo(() => {
    if (deliveredCustomers.length === 0) return 0;
    let totalDays = 0;
    deliveredCustomers.forEach(c => {
      const start = new Date(c.created_at).getTime();
      const end = new Date(c.updated_at || Date.now()).getTime();
      const days = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
      totalDays += days;
    });
    return Math.round(totalDays / deliveredCustomers.length);
  }, [deliveredCustomers]);

  return (
    <div style={{ padding: '24px', background: 'var(--crm-bg-main)', minHeight: '100vh', color: 'var(--crm-text-primary)' }}>
      {/* Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {onBack && (
              <button 
                onClick={onBack}
                style={{
                  background: 'var(--crm-bg-card)',
                  border: '1px solid var(--crm-border-color)',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'var(--crm-text-primary)',
                  fontWeight: 600
                }}
              >
                ← Back
              </button>
            )}
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: 'var(--crm-text-primary)' }}>
              🚚 Completed Deliveries Report
            </h1>
          </div>
          <p style={{ margin: '4px 0 0 0', fontSize: '13.5px', color: 'var(--crm-text-secondary)' }}>
            Comprehensive status of all vehicles successfully processed and delivered to customers.
          </p>
        </div>

        <button 
          onClick={fetchDeliveredCustomers}
          disabled={loading}
          style={{
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 18px',
            fontWeight: 700,
            fontSize: '14px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          🔄 Refresh Deliveries Data
        </button>
      </div>

      {/* KPI Stats Bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ background: 'var(--crm-bg-card)', borderRadius: '12px', padding: '18px', border: '1px solid var(--crm-border-color)', borderLeft: '5px solid #10B981', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12.5px', color: 'var(--crm-text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>Total Vehicles Delivered</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#10B981', marginTop: '6px' }}>{totalDelivered} 🎉</div>
          <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginTop: '4px' }}>100% Completed Workflows</div>
        </div>

        <div style={{ background: 'var(--crm-bg-card)', borderRadius: '12px', padding: '18px', border: '1px solid var(--crm-border-color)', borderLeft: '5px solid #3B82F6', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12.5px', color: 'var(--crm-text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>Delivered This Month</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#3B82F6', marginTop: '6px' }}>{thisMonthDelivered} 🚗</div>
          <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginTop: '4px' }}>Current Month Deliveries</div>
        </div>

        <div style={{ background: 'var(--crm-bg-card)', borderRadius: '12px', padding: '18px', border: '1px solid var(--crm-border-color)', borderLeft: '5px solid #8B5CF6', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12.5px', color: 'var(--crm-text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>Avg Days to Delivery</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#8B5CF6', marginTop: '6px' }}>{avgDaysToDelivery} Days ⏱️</div>
          <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginTop: '4px' }}>Booking to Final Key Handover</div>
        </div>

        <div style={{ background: 'var(--crm-bg-card)', borderRadius: '12px', padding: '18px', border: '1px solid var(--crm-border-color)', borderLeft: '5px solid #F59E0B', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12.5px', color: 'var(--crm-text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>Completion Rate</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#F59E0B', marginTop: '6px' }}>
            {customers.length > 0 ? `${Math.round((deliveredCustomers.length / customers.length) * 100)}%` : '100%'} 💯
          </div>
          <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginTop: '4px' }}>Successful Lead Conversion</div>
        </div>
      </div>

      {/* Search & Filter Toolbar */}
      <div style={{ background: 'var(--crm-bg-card)', borderRadius: '12px', padding: '16px', border: '1px solid var(--crm-border-color)', marginBottom: '20px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: '240px' }}>
          <input
            type="text"
            placeholder="🔍 Search customer name, phone, or car details..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid var(--crm-border-color)',
              background: 'var(--crm-bg-input, #fff)',
              color: 'var(--crm-text-primary)',
              fontSize: '14px'
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--crm-text-secondary)' }}>Car Model:</label>
          <select
            value={carFilter}
            onChange={(e) => setCarFilter(e.target.value)}
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid var(--crm-border-color)',
              background: 'var(--crm-bg-input, #fff)',
              color: 'var(--crm-text-primary)',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Models</option>
            <option value="ertiga">Ertiga</option>
            <option value="dzire">Dzire</option>
            <option value="aura">Aura</option>
            <option value="wagon">Wagon R</option>
          </select>
        </div>
      </div>

      {/* Delivered Leads Table Container */}
      <div style={{ background: 'var(--crm-bg-card)', borderRadius: '12px', border: '1px solid var(--crm-border-color)', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--crm-text-secondary)' }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>🔄</div>
            <div>Loading completed deliveries...</div>
          </div>
        ) : filteredList.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>📦</div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--crm-text-secondary)' }}>No completed deliveries found matching your search.</div>
            <p style={{ fontSize: '13px', margin: '6px 0 0 0' }}>Once leads complete all 10 workflow steps up to Delivery, they will be listed here.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13.5px' }}>
              <thead>
                <tr style={{ background: 'var(--crm-bg-hover, #F9FAFB)', borderBottom: '1px solid var(--crm-border-color)' }}>
                  <th style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--crm-text-secondary)' }}>#</th>
                  <th style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--crm-text-secondary)' }}>Customer Details</th>
                  <th style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--crm-text-secondary)' }}>Vehicle / Car Details</th>
                  <th style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--crm-text-secondary)' }}>Branch</th>
                  <th style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--crm-text-secondary)' }}>Delivery Status & Steps</th>
                  <th style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--crm-text-secondary)' }}>Total Processing Time</th>
                  <th style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--crm-text-secondary)', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((cust, idx) => {
                  const steps = cust.crm_workflow_steps || cust.workflow_steps || [];
                  const completedStepsCount = steps.filter((s: any) => s.status === 'Done').length;
                  const start = new Date(cust.created_at).getTime();
                  const end = new Date(cust.updated_at || Date.now()).getTime();
                  const processingDays = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));

                  return (
                    <tr 
                      key={cust.id || idx}
                      style={{ 
                        borderBottom: '1px solid var(--crm-border-color)',
                        transition: 'background 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--crm-bg-hover, #F3F4F6)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '14px 16px', fontWeight: 600, color: 'var(--crm-text-muted)' }}>{idx + 1}</td>
                      
                      {/* Customer Name & Phone */}
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--crm-text-primary)' }}>
                          {renderCustomerNameWithTravels(cust.customer_name)}
                        </div>
                        <div style={{ fontSize: '12.5px', color: 'var(--crm-text-secondary)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          📞 {cust.phone || 'N/A'}
                        </div>
                      </td>

                      {/* Vehicle Details */}
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ 
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          background: 'rgba(59, 130, 246, 0.1)',
                          color: '#2563EB',
                          fontWeight: 700,
                          fontSize: '12.5px'
                        }}>
                          🚘 {cust.car_details || cust.interested_car || 'Commercial Vehicle'}
                        </span>
                      </td>

                      {/* Branch Name */}
                      <td style={{ padding: '14px 16px', fontWeight: 600, color: 'var(--crm-text-secondary)' }}>
                        🏢 {cust.branch_name || cust.crm_branches?.name || 'Main Branch'}
                      </td>

                      {/* 10 Step Delivery Pipeline Status */}
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                          <span style={{ 
                            background: '#D1FAE5',
                            color: '#065F46',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11.5px',
                            fontWeight: 700
                          }}>
                            ✅ Delivery Complete ({completedStepsCount}/10 Steps)
                          </span>
                        </div>
                        
                        {/* Mini Pipeline Indicators */}
                        <div style={{ display: 'flex', gap: '3px', marginTop: '4px' }}>
                          {WORKFLOW_STEPS.map((stepName, i) => {
                            const stepObj = steps.find((s: any) => s.step_name === stepName);
                            const isDone = stepObj && stepObj.status === 'Done';
                            return (
                              <div
                                key={i}
                                title={`${stepName}: ${isDone ? 'Completed' : 'Pending'}`}
                                style={{
                                  height: '6px',
                                  flex: 1,
                                  borderRadius: '3px',
                                  backgroundColor: isDone ? '#10B981' : '#E5E7EB'
                                }}
                              />
                            );
                          })}
                        </div>
                      </td>

                      {/* Total Processing Time */}
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 700, color: 'var(--crm-text-primary)' }}>
                          ⏱️ {processingDays} Days
                        </div>
                        <div style={{ fontSize: '11.5px', color: 'var(--crm-text-muted)' }}>
                          Booked: {new Date(cust.created_at).toLocaleDateString('en-IN')}
                        </div>
                      </td>

                      {/* Action */}
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        <button
                          onClick={() => setSelectedCustomerId(cust.id)}
                          style={{
                            background: 'var(--crm-bg-card)',
                            border: '1px solid #10B981',
                            color: '#059669',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12.5px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          👁️ View Complete Journey
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Customer Journey Modal */}
      {selectedCustomerId && (
        <CustomerJourneyModal
          customerId={selectedCustomerId}
          onClose={() => setSelectedCustomerId(null)}
        />
      )}
    </div>
  );
};

export default CompletedDeliveries;
